import React, { useState, useEffect, useRef } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Button, ButtonGroup } from "reactstrap";

import { LangItem } from "../../../models/lang";
import { UserItem } from "../../../models/user";
import {
  getAuthCookie,
  setAuthCookie,
} from "../../../middleware/cookie-manager";
import { GET_LANGUAGES, UPDATELANGS } from "./queries";

type LanguagesProps = {
  languages: LangItem[];
  activeOnly?: boolean;
  setReload?: React.Dispatch<React.SetStateAction<boolean>>;
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
};

export const Languages: React.FC<LanguagesProps> = ({
  languages,
  activeOnly,
  setReload,
  setLoading,
}) => {
  const user = getAuthCookie(true);
  const display_langs = languages.map((l) => l);
  const { data, loading } = useQuery<{ languages: LangItem[] }>(GET_LANGUAGES);
  const [langs, setLangs] = useState(display_langs);
  const [setLang, { loading: loadingupdate }] = useMutation(UPDATELANGS);
  const [timer, setTimer] = useState<any>(null);

  const timerRef = useRef(timer);
  timerRef.current = timer;
  const langsRef = useRef(langs);
  langsRef.current = langs;
  const setLangRef = useRef(setLang);
  setLangRef.current = setLang;

  useEffect(() => {
    if (data) {
      setLangs(data.languages);
    }
  }, [data]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        const selected_ids = langsRef.current
          .filter((l) => l.active)
          .map((l) => l.id);
        if (selected_ids.length > 0) {
          setLangRef.current({ variables: { langs: selected_ids } });
        }
      }
    };
  }, []);

  const handleChangeLang = (ev: { currentTarget: HTMLButtonElement }) => {
    if (timer) {
      clearTimeout(timer);
    }
    const set_langs = langs.map((l) => ({ ...l }));
    const lang_id = ev.currentTarget.dataset.lang_id;
    const active = ev.currentTarget.dataset.active === "true" ? false : true;
    if (!lang_id) {
      return;
    }
    const index_of = set_langs.findIndex((l: LangItem) => l.id === lang_id);
    if (index_of < 0) {
      return;
    }
    setLoading!(true);
    const set_user = { ...user };
    if (active) {
      set_user.lang_id = lang_id;
    } else if (!active && set_user.lang_id === lang_id) {
      set_user.lang_id =
        set_langs.find((l) => l.user_active && l.id !== lang_id)?.id || "";
    }
    setAuthCookie(set_user as UserItem);
    setLoading!(false);
    set_langs[index_of].user_active = active;
    setLangs(set_langs);

    const selected_ids = set_langs
      .filter((l) => l.user_active)
      .map((l) => l.id);

    if (selected_ids.length === 0) {
      return;
    }
    const newTimer = setTimeout(() => {
      setLang({
        variables: { langs: selected_ids, lang_id: set_user.lang_id },
      });
      setReload!(true);
    }, 2000);
    setTimer(newTimer);
  };

  ///return null if display_langs is empty
  if (display_langs.length === 0) return null;

  return (
    <>
      <p className={activeOnly ? "mb-0" : ""}>Flangoo languages</p>
      <small className="help mb-2 d-block">
        What Subject Do You Teach?{" "}
        {loading || loadingupdate ? (
          <i className="fal fa-spinner-third fa-spin ml-2"></i>
        ) : null}
        <br />
        The selected languages will be used to display the Flangoo interface and
        content. You can select multiple languages, but only one will be active
        at a time.
      </small>
      <ButtonGroup>
        {languages && languages.length > 0
          ? langs.map((lang) => (
              <Button
                disabled={loading || loadingupdate}
                color={lang.active ? "primary" : "outline-light "}
                className="no_focus pb-3"
                active={lang.user_active}
                key={`Button_${lang.id}`}
                data-lang_id={lang.id}
                onClick={handleChangeLang}
                data-active={lang.user_active ? "true" : "false"}
              >
                {user.lang_id === lang.id && lang.user_active ? (
                  <small className="form-text active_badge ">Active</small>
                ) : null}
                <i
                  className={`fal mr-2 ${
                    lang.user_active ? "fa-check-square" : "fa-square"
                  }`}
                ></i>

                {lang.name}
              </Button>
            ))
          : null}
      </ButtonGroup>
    </>
  );
};
